import React from "react";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { FocusRestoreContext } from "./SideMenuContexts";

export interface SideDrawerProps {
    /** Heading of the drawer, also used to name the drawer and the collapsed rail for screen readers. */
    title: string;
    /** Initial open state. The drawer also opens or closes whenever this changes. */
    startOpen: boolean;
    /** Icon on the collapsed rail, rendered as is inside the relative 24px wide rail button. Position it yourself, e.g. "absolute top-1 left-1". */
    collapsedIcon: React.ReactNode;
    /** Classes for the outer wrapper, e.g. "fixed md:relative". */
    className?: string;
    /** `data-tour` attribute for the drawer panel. */
    dataTour?: string;
    /** When set, `window[controlsKey]` gets `{ open, close }` while the drawer is mounted, so tours can drive it. */
    controlsKey?: string;
    /** Drawer body. It stays mounted while the drawer is closed (the panel is only hidden), so effects in it keep running. */
    children: React.ReactNode;
}

interface DrawerControls {
    open: () => void;
    close: () => void;
}

/** Below Tailwind's `md` breakpoint, which the pages use to switch the drawer wrapper between fixed and relative. */
export const OVERLAY_MEDIA_QUERY = "(max-width: 767.98px)";
const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]";
const TREE_TAB_STOP = '[role="treeitem"][tabindex="0"]';

const getOverlayQuery = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(OVERLAY_MEDIA_QUERY) : undefined;

/** Narrow screens get an overlay drawer with a backdrop, wider ones an inline drawer that pushes the content aside. */
function useIsOverlay() {
    const [isOverlay, setIsOverlay] = React.useState(() => getOverlayQuery()?.matches ?? false);

    React.useEffect(() => {
        const query = getOverlayQuery();
        if (!query) {
            return;
        }

        const handleChange = () => setIsOverlay(query.matches);
        handleChange();
        query.addEventListener("change", handleChange);
        return () => query.removeEventListener("change", handleChange);
    }, []);

    return isOverlay;
}

/**
 * Side menu with a collapsed 24px rail and a 320px panel (header with title and close button, scrollable body).
 * Styled with the Fluent theme CSS variables from the surrounding FluentProvider so it matches Fluent's Drawer.
 */
export function SideDrawer({ title, startOpen, collapsedIcon, className, dataTour, controlsKey, children }: SideDrawerProps) {
    const [isOpen, setIsOpen] = React.useState(startOpen);
    const [isScrolled, setIsScrolled] = React.useState(false);
    const isOverlay = useIsOverlay();
    const railRef = React.useRef<HTMLButtonElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);
    const wasOpen = React.useRef(isOpen);
    // Starts out false so a drawer that mounts open as the modal overlay also takes focus.
    const wasOverlay = React.useRef(false);
    const focusPanelOnOpen = React.useRef(false);
    const titleId = React.useId();

    React.useEffect(() => { setIsOpen(startOpen) }, [startOpen]);

    React.useEffect(() => {
        if (!controlsKey) {
            return;
        }

        const controls: DrawerControls = {
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
        };
        (window as any)[controlsKey] = controls;
        return () => {
            if ((window as any)[controlsKey] === controls) {
                delete (window as any)[controlsKey];
            }
        };
    }, [controlsKey]);

    /** Focuses the body's first tree on its active row, else the close button, else the panel itself. */
    const focusPanel = React.useCallback(() => {
        const target = panelRef.current?.querySelector<HTMLElement>(TREE_TAB_STOP) ?? closeButtonRef.current ?? panelRef.current;
        target?.focus({ preventScroll: true });
    }, []);

    const restoreFocus = React.useCallback(() => {
        if (isOpen) {
            focusPanel();
        }
    }, [focusPanel, isOpen]);

    // Opening from the rail moves focus into the panel (the rail itself disappears), and so does any opening of the modal
    // overlay. Closing while focus is in the panel moves it back to the rail instead of losing it.
    React.useLayoutEffect(() => {
        if (isOpen === wasOpen.current) {
            return;
        }
        wasOpen.current = isOpen;

        if (isOpen) {
            if (focusPanelOnOpen.current || isOverlay) {
                focusPanel();
            }
            focusPanelOnOpen.current = false;
            return;
        }

        const active = document.activeElement;
        if (!active || active === document.body || panelRef.current?.contains(active)) {
            railRef.current?.focus({ preventScroll: true });
        }
    }, [isOpen, isOverlay, focusPanel]);

    // An open drawer that turns into the modal overlay (the window got narrower, or it mounted open on a narrow
    // screen) takes focus like a freshly opened one.
    React.useLayoutEffect(() => {
        if (isOverlay === wasOverlay.current) {
            return;
        }
        wasOverlay.current = isOverlay;

        if (isOverlay && isOpen && !panelRef.current?.contains(document.activeElement)) {
            focusPanel();
        }
    }, [isOverlay, isOpen, focusPanel]);

    React.useLayoutEffect(() => {
        // Inline drawer width changes don't fire a window resize, but our ECharts components
        // only recalc on window resize. Fire one before paint to avoid a visible "snap".
        window.dispatchEvent(new Event("resize"));
        if (typeof window.requestAnimationFrame !== "function") {
            return;
        }

        const frame = window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
        return () => window.cancelAnimationFrame(frame);
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen || !isOverlay) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            // Menus inside the drawer handle their own Escape first.
            if (event.key === "Escape" && !event.defaultPrevented) {
                setIsOpen(false);
            }
        };
        // Like Fluent's modal drawer, the page behind the overlay doesn't scroll.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, isOverlay]);

    // The overlay drawer is modal, so Tab cycles within it.
    const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const panel = panelRef.current;
        if (!isOverlay || event.key !== "Tab" || !panel) {
            return;
        }

        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.tabIndex >= 0);
        if (focusable.length === 0) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const panelLayoutClass = isOverlay
        ? "fixed inset-y-0 left-0 border-[color:var(--colorTransparentStroke)]"
        : "relative h-full border-[color:var(--colorNeutralBackground3)]";

    return (
        // The overlay sits above the other fixed page chrome, such as the assistant rail on the right (z-50).
        <div className={`${className ?? ""} h-8 ${isOverlay ? "z-[60]" : "z-20"}`}>
            <div className="fixed h-screen">
                {!isOpen && (
                    <button
                        ref={railRef}
                        type="button"
                        aria-label={`Open ${title}`}
                        title={`Open ${title}`}
                        className="relative h-screen w-6 bg-primary-light dark:bg-primary-dark hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--colorStrokeFocus2)]"
                        onClick={() => {
                            focusPanelOnOpen.current = true;
                            setIsOpen(true);
                        }}
                    >
                        {collapsedIcon}
                    </button>
                )}

                {isOpen && isOverlay && (
                    <div
                        aria-hidden
                        className="fixed inset-0 touch-none bg-[color:var(--colorBackgroundOverlay,rgba(0,0,0,0.4))] animate-drawer-backdrop-in motion-reduce:animate-none"
                        onClick={() => setIsOpen(false)}
                    />
                )}

                {/* Hidden rather than unmounted while closed, so the body keeps its state and effects. */}
                <div
                    ref={panelRef}
                    role={isOverlay ? "dialog" : "complementary"}
                    aria-modal={isOverlay ? true : undefined}
                    aria-labelledby={titleId}
                    tabIndex={-1}
                    data-tour={dataTour}
                    className={`${isOpen ? "flex" : "hidden"} ${panelLayoutClass} w-80 max-w-[100vw] flex-col overflow-hidden border-r bg-[var(--colorNeutralBackground1)] text-[color:var(--colorNeutralForeground1)] outline-none animate-drawer-in motion-reduce:animate-none`}
                    style={isOverlay ? { boxShadow: "var(--shadow64)" } : undefined}
                    onKeyDown={handlePanelKeyDown}
                >
                    <div className="relative flex shrink-0 items-center justify-between gap-2 px-6 pb-2 pt-6">
                        <h2 id={titleId} className="text-xl font-semibold">{title}</h2>
                        <button
                            ref={closeButtonRef}
                            type="button"
                            aria-label="Close"
                            className="-mr-2 flex h-8 w-8 items-center justify-center self-start rounded text-[color:var(--colorNeutralForeground2)] outline-none [@media(hover:hover)]:hover:bg-[var(--colorSubtleBackgroundHover)] [@media(hover:hover)]:hover:text-[color:var(--colorNeutralForeground2Hover)] active:bg-[var(--colorSubtleBackgroundPressed)] active:text-[color:var(--colorNeutralForeground2Pressed)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--colorStrokeFocus2)] focus-visible:shadow-[inset_0_0_0_1px_var(--colorStrokeFocus1)]"
                            onClick={() => setIsOpen(false)}
                        >
                            <Dismiss24Regular />
                        </button>
                        {/* Like Fluent's DrawerHeader: a separator shows once the body is scrolled. */}
                        <div
                            aria-hidden
                            className={`absolute inset-x-0 bottom-0 h-px bg-[var(--colorNeutralStroke1)] transition-opacity duration-200 ${isScrolled ? "opacity-100" : "opacity-0"}`}
                        />
                    </div>
                    {/* A stable scrollbar gutter, so a scrollbar appearing never re-wraps the labels. */}
                    <div
                        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-[25px] pt-1 [scrollbar-gutter:stable]"
                        onScroll={(event) => setIsScrolled(event.currentTarget.scrollTop > 0)}
                    >
                        <FocusRestoreContext.Provider value={restoreFocus}>
                            {children}
                        </FocusRestoreContext.Provider>
                    </div>
                </div>
            </div>
            <div className={isOpen ? "w-80" : "w-6"} />
        </div>
    );
}
